/**
 * The shell every chapter is drawn in — stored or streaming (the story feed's chapter-as-scene, the live turn's
 * live turn).
 *
 * This file exists for one reason: **a chapter that streamed in and the same chapter after a
 * reload must be indistinguishable.** Slice 1 had only one caller and could put the layout in
 * `ChapterScene`; slice 2 adds a second caller whose narration is half-written and whose
 * dateline is one segment long, and two components rendering "a chapter" is how they start
 * drifting a padding value at a time. So the layout lives here, with slots, and both callers
 * are thin.
 *
 * The reading order is the order it happened in, which is the whole of chapter-as-scene:
 *
 * ─────────────────── Chapter 12 · Day 3, evening · Rust Market dateline
 * › I search the collapsed stall the player's words
 * The tarpaulin gives with a sound like a held breath… the story
 * ▸ +1 canned peaches ▸ −8 stamina the ticker
 *
 * **Section labels are gone.** Not moved, not shrunk — gone. The three voices are three faces:
 * mono for the action (the one thing that is the player's), serif for narration (the world),
 * sans for everything meta. That is the whole mechanism, and it is why the typography in
 * `tokens.css` had to land before this file existed rather than after.
 */
import { useId } from 'react';

interface SceneProps {
  /**
   * `chapter-12`. The journal jumps to it and `scroll-mt` keeps the dateline clear of
   * a sticky top edge when it does. A live turn has no such anchor — nothing can link to a
   * chapter that does not exist yet — which is why it is optional and the heading id is not.
   */
  id?: string;
  /** Joined with `·`. A live turn has only `Chapter 25` — the rest is not known until commit. */
  dateline: string[];
  /** Null on the opening chapter: there was nothing to act on yet. */
  action: string | null;
  /** The narration — `Prose`, with or without a streaming cursor. */
  children: React.ReactNode;
  /** "What the DM saw", on a stored chapter only. */
  notes?: React.ReactNode;
  /** The change ticker, in whichever of its two modes the caller is in. */
  footer?: React.ReactNode;
}

export function Scene({ id, dateline, action, children, notes, footer }: SceneProps) {
  // The feed is a wall of asynchronous text, so every scene is named by its dateline for
  // anyone navigating by landmark — the streaming one included, which is the one a screen
  // reader most needs to be able to find.
  const headingId = useId();

  return (
    <article
      id={id}
      aria-labelledby={headingId}
      className="mt-[var(--space-chapter)] scroll-mt-8 first:mt-0"
    >
      {/*
        The rule and the label are one row: a hairline that runs to the label and stops. A box
        around the chapter would reintroduce exactly the "form" this section deletes, and
        whitespace plus a rule is what the typographic pass asks for instead.
      */}
      <header className="mb-7 flex items-center gap-4">
        <span aria-hidden="true" className="bg-rule h-px w-8 shrink-0" />
        <h2
          id={headingId}
          className="text-fg-muted font-mono tracking-wider"
          style={{ fontSize: 'var(--text-meta)' }}
        >
          {dateline.join(' · ')}
        </h2>
        <span aria-hidden="true" className="bg-rule h-px grow" />
      </header>

      {action !== null ? (
        <p
          className="text-fg-muted mb-6 flex gap-2 font-mono"
          style={{ fontSize: 'var(--text-action)' }}
        >
          <span aria-hidden="true" className="text-fg-faint select-none">
            ›
          </span>
          <span>{action}</span>
        </p>
      ) : null}

      {children}

      {notes}
      {footer}
    </article>
  );
}
